import React from 'react';
import {
  buildReserveSlotKey,
  checkTicketsSelectedAreAvailable,
  getSlotSpaceStatus,
  getTicketPriceAfterOverrides,
} from '../../../../../../../shared/lib/reserve';
import { ReservableTicket } from '../../../../../../../shared/types/reservable';
import { ReserveCacheSlot } from '../../../../../../../shared/types/reserveCache';
import Button from './Button';
import RectangleCard from './RectangleCard';
import {
  DateOption,
  TicketSelections,
  TimeOption,
} from '../../../../../../../shared/types/reserve';

interface TicketPickerProps {
  tickets: ReservableTicket[];
  selected: TicketSelections;
  onSelected: (selections: TicketSelections) => void;
  isRental?: boolean;
  autoAssignRental?: boolean;
  isNewSession?: boolean;
  selectedSlot?: ReserveCacheSlot;
  ticketReservationLimits?: Record<string, number>;
  onExploreDates: (selectedRental: string | undefined) => void;

  selectedDate?: DateOption;
  selectedTime?: TimeOption;
  timeslots: ReserveCacheSlot[];
}

export function TicketPicker(props: TicketPickerProps) {
  const {
    tickets,
    selected,
    onSelected,
    isRental,
    autoAssignRental,
    isNewSession,
    selectedSlot,
    ticketReservationLimits,
    selectedDate,
    selectedTime,
    timeslots,
    onExploreDates,
  } = props;

  function setAmount(ticketId: string, amount: number) {
    if (isRental) {
      // toggle on and off, force others 0
      onSelected({
        quantities: tickets.reduce(
          (acc, t) => {
            acc[t._id] = t._id === ticketId ? (amount > 0 ? 1 : 0) : 0;
            return acc;
          },
          {} as Record<string, number>
        ),
      });
    } else {
      const current = selected.quantities[ticketId] ?? 0;
      const delta = amount - current;

      onSelected({
        quantities: {
          ...selected.quantities,
          [ticketId]: Math.max(0, current + delta),
        },
      });
    }
  }

  if (isRental && autoAssignRental) {
    const ticketsAvailable =
      selectedDate && selectedTime
        ? tickets.filter(ticket =>
            timeslots.some(
              s =>
                s.slotKey ===
                  buildReserveSlotKey(
                    { date: selectedDate.key, rental: ticket._id, host: selectedTime.host },
                    selectedTime.time,
                    selectedTime.duration
                  ) && getSlotSpaceStatus(s).dynamicSpotsLeft > 0
            )
          )
        : [];

    const firstAvailable = ticketsAvailable[0];
    const availableCount = ticketsAvailable.length;
    const isSelected = firstAvailable ? (selected.quantities[firstAvailable._id] ?? 0) > 0 : false;

    return (
      <RectangleCard
        name="Next Available"
        description={
          firstAvailable
            ? `${availableCount} available — one will be assigned to you.`
            : 'No rentals are available for this date and time.'
        }
        singleMode
        buttonLabel="Select"
        amount={isSelected ? 1 : 0}
        setAmount={firstAvailable ? amount => setAmount(firstAvailable._id, amount) : undefined}
        buttonOverride={
          !firstAvailable ? (
            <Button
              onClick={() => onExploreDates(undefined)}
              icon={['fass', 'calendar']}
              variant="borderlike"
              inverse
              className="text-active-i"
              disabled={!isNewSession}
            >
              Explore Dates
            </Button>
          ) : undefined
        }
      />
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4">
      {tickets.map(ticket => {
        const available =
          selectedDate &&
          selectedTime &&
          timeslots.some(
            s =>
              s.slotKey ===
                buildReserveSlotKey(
                  {
                    date: selectedDate.key,
                    rental: isRental ? ticket._id : undefined,
                    host: selectedTime.host,
                  },
                  selectedTime.time,
                  selectedTime.duration
                ) && getSlotSpaceStatus(s).dynamicSpotsLeft > 0
          );

        return (
          <RectangleCard
            key={ticket._id}
            name={ticket.name}
            description={ticket.description}
            price={
              selectedSlot
                ? getTicketPriceAfterOverrides(ticket, selectedSlot.priceOverrides)
                : ticket.price
            }
            image={ticket.images[0]}
            amount={selected.quantities[ticket._id] ?? 0}
            singleMode={isRental}
            error={
              isNewSession && selectedSlot
                ? checkTicketsSelectedAreAvailable(
                    ticket._id,
                    selectedSlot,
                    selected.quantities,
                    ticketReservationLimits ?? {}
                  )
                : undefined
            }
            buttonOverride={
              !available ? (
                <Button
                  onClick={() => onExploreDates(ticket._id)}
                  icon={['fass', 'calendar']}
                  variant="borderlike"
                  inverse
                  className="text-active-i"
                  disabled={!isNewSession}
                >
                  Explore Dates
                </Button>
              ) : undefined
            }
            buttonLabel={isRental ? 'Select' : undefined}
            setAmount={amount => setAmount(ticket._id, amount)}
          />
        );
      })}
    </div>
  );
}
